import * as React from "react";
import { useRef, useMemo, useState, useEffect } from "react";
import Select from "@/components/Select";
import IconTrash1 from "@/icons/IconTrash1";
import VariablePicker from "@/components/VariablePicker";
import {
  FormValidator,
  QuestionType,
  questionTypeDefs,
  SurveyQuestion
} from "@velaro/velaro-component-lib";
import useSubscriptions from "@/hooks/useSubscriptions";
import { languageOptionsList } from "@/constants/languages";
import ReactSelect from "react-select";

// Extended question type with fields beyond the shared component-lib type
export interface VisibilityCondition {
  questionIndex: number;
  operator:
    | "equals"
    | "not_equals"
    | "contains"
    | "starts_with"
    | "is_empty"
    | "is_not_empty";
  value: string;
}

export interface ExtSurveyQuestion extends SurveyQuestion {
  satisfactionMetricType?: string;
  visibilityCondition?: VisibilityCondition | null;
}

const defaultLabelForType: Record<string, string> = {
  name: "What is your name?",
  email: "What is your email address?",
  text: "",
  textArea: "",
  choice: "",
  checkbox: "",
  star: "How would you rate us?",
  netPromoterScore: "How likely are you to recommend us? (0-10)",
  crmField: "",
  language: "What is your preferred language?",
  label: ""
};

const TYPE_BADGE: Record<
  string,
  { label: string; colors: string }
> = {
  name: { label: "Name", colors: "bg-blue-100 text-blue-700" },
  email: { label: "Email", colors: "bg-blue-100 text-blue-700" },
  text: { label: "Text", colors: "bg-slate-100 text-slate-600" },
  textArea: { label: "Long text", colors: "bg-slate-100 text-slate-600" },
  choice: { label: "Dropdown", colors: "bg-violet-100 text-violet-700" },
  checkbox: { label: "Checkbox", colors: "bg-purple-100 text-purple-700" },
  star: { label: "Star rating", colors: "bg-amber-100 text-amber-700" },
  netPromoterScore: { label: "NPS", colors: "bg-orange-100 text-orange-700" },
  crmField: { label: "CRM field", colors: "bg-teal-100 text-teal-700" },
  language: { label: "Language", colors: "bg-emerald-100 text-emerald-700" },
  label: { label: "Label / divider", colors: "bg-slate-100 text-slate-500" }
};

const SATISFACTION_OPTIONS = [
  { value: "", label: "— not a metric —" },
  { value: "nps", label: "NPS (0–10)" },
  { value: "csat", label: "CSAT (1–5)" },
  { value: "star_rating", label: "Star rating (1–5)" },
  { value: "thumbs", label: "Thumbs up/down" },
  { value: "ces", label: "CES (1–7)" }
];

const OPERATOR_OPTIONS = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "doesn't equal" },
  { value: "contains", label: "contains" },
  { value: "starts_with", label: "starts with" },
  { value: "is_empty", label: "is empty" },
  { value: "is_not_empty", label: "is answered" }
];

function hasFixedVariable(type: QuestionType | string) {
  switch (type) {
    case "name":
    case "email":
    case "crmField":
    case "language":
      return true;
    default:
      return false;
  }
}

function canTrackSatisfaction(type: string) {
  return type === "star" || type === "netPromoterScore";
}

interface Props {
  question: ExtSurveyQuestion;
  errors: Record<string, string>;
  index: number;
  allQuestions: ExtSurveyQuestion[];
  netSuiteMappings?: any[];
  loadingMappings?: boolean;
  isFirst: boolean;
  isLast: boolean;
  update(question: ExtSurveyQuestion): void;
  remove(): void;
  moveUp(): void;
  moveDown(): void;
}

export default function QuestionEditor({
  question,
  errors,
  index,
  allQuestions,
  netSuiteMappings = [],
  loadingMappings = false,
  isFirst,
  isLast,
  update,
  remove,
  moveUp,
  moveDown
}: Props) {
  const variableFixed = hasFixedVariable(question.type);
  const { subscription } = useSubscriptions();
  const labelInputRef = useRef<HTMLInputElement>(null);
  const [showSkipLogic, setShowSkipLogic] = useState(
    !!question.visibilityCondition
  );

  // Sync state when the question prop changes (e.g. a different question lands at this index slot)
  useEffect(() => {
    setShowSkipLogic(!!question.visibilityCondition);
  }, [question.visibilityCondition]);

  const availableQuestionTypes = useMemo(() => {
    const allOptions = Object.entries(questionTypeDefs).map(
      ([value, label]) => ({ value, label })
    );
    if (!subscription?.enableNetsuite) {
      return allOptions.filter((option) => option.value !== "crmField");
    }
    return allOptions;
  }, [subscription?.enableNetsuite]);

  const netSuiteOptions = useMemo(() => {
    if (loadingMappings) return [{ label: "Loading...", value: "" }];
    if (!netSuiteMappings || netSuiteMappings.length === 0)
      return [{ label: "No NetSuite fields available", value: "" }];
    return netSuiteMappings.map((mapping) => ({
      label: `${mapping.recordType} - ${mapping.netSuiteField}`,
      value: mapping.netSuiteField,
      recordType: mapping.recordType,
      netSuiteField: mapping.netSuiteField
    }));
  }, [netSuiteMappings, loadingMappings]);

  const optionsList = useMemo(
    () => (question.options ? question.options.join("\n") : ""),
    [question.options]
  );

  const typeHasOptions =
    question.type === "choice" ||
    question.type === "checkbox" ||
    question.type === "crmField" ||
    (question.type as string) === "language";

  const typeHasInput = question.type !== "label";

  // Questions before this one that have a variable (can be watched)
  const watchableQuestions = allQuestions
    .slice(0, index)
    .map((q, qi) => ({ q, qi }))
    .filter(({ q }) => q.type !== "label");

  function changeType(type: QuestionType | string) {
    const wasFixed = variableFixed;
    const isFixed = hasFixedVariable(type);
    const questionType = type as QuestionType;

    const oldDefault = defaultLabelForType[question.type] ?? "";
    const shouldUpdateLabel = !question.text || question.text === oldDefault;
    const newLabel = shouldUpdateLabel
      ? (defaultLabelForType[type] ?? "")
      : question.text;

    if (type === "language") {
      update({ ...question, type: questionType, text: newLabel, variable: "language", options: [] });
      return;
    }
    if (isFixed) {
      update({ ...question, type: questionType, text: newLabel, variable: type });
      return;
    }
    if (wasFixed) {
      update({ ...question, type: questionType, text: newLabel, variable: "" });
      return;
    }
    update({ ...question, type: questionType, text: newLabel });
  }

  function insertVariableIntoLabel(variable: string) {
    const input = labelInputRef.current;
    if (!input) {
      update({ ...question, text: question.text + variable });
      return;
    }
    const start = input.selectionStart ?? question.text.length;
    const end = input.selectionEnd ?? question.text.length;
    const newText =
      question.text.slice(0, start) + variable + question.text.slice(end);
    update({ ...question, text: newText });
    requestAnimationFrame(() => {
      input.setSelectionRange(
        start + variable.length,
        start + variable.length
      );
      input.focus();
    });
  }

  const cond = question.visibilityCondition;

  function toggleSkipLogic() {
    if (showSkipLogic) {
      update({ ...question, visibilityCondition: null });
      setShowSkipLogic(false);
    } else {
      const firstWatchable = watchableQuestions[0];
      update({
        ...question,
        visibilityCondition: {
          questionIndex: firstWatchable?.qi ?? 0,
          operator: "equals",
          value: ""
        }
      });
      setShowSkipLogic(true);
    }
  }

  function updateCondition(patch: Partial<VisibilityCondition>) {
    update({
      ...question,
      visibilityCondition: { ...cond!, ...patch }
    });
  }

  const badge = TYPE_BADGE[question.type] ?? {
    label: question.type,
    colors: "bg-slate-100 text-slate-500"
  };
  const variableDisplay =
    (variableFixed ? question.variable : question.variable) || null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-150">
      {/* ── Header row: reorder + type + trash ── */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        {/* Move up/down */}
        <div className="flex flex-col gap-0.5 shrink-0">
          <button
            type="button"
            onClick={moveUp}
            disabled={isFirst}
            aria-label="Move question up"
            className="w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l5 5a1 1 0 01-1.414 1.414L10 5.414 5.707 9.707a1 1 0 01-1.414-1.414l5-5A1 1 0 0110 3z" clipRule="evenodd" />
            </svg>
          </button>
          <button
            type="button"
            onClick={moveDown}
            disabled={isLast}
            aria-label="Move question down"
            className="w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M10 17a1 1 0 01-.707-.293l-5-5a1 1 0 011.414-1.414L10 14.586l4.293-4.293a1 1 0 011.414 1.414l-5 5A1 1 0 0110 17z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Question number */}
        <span className="text-xs font-semibold text-slate-300 w-5 shrink-0 text-center">
          {index + 1}
        </span>

        {/* Type selector */}
        <div className="shrink-0">
          <Select
            options={availableQuestionTypes}
            className="input text-sm py-1"
            value={question.type || "text"}
            onChange={changeType}
          />
        </div>

        {/* Type badge */}
        <span
          className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${badge.colors}`}
        >
          {badge.label}
        </span>

        <div className="flex-1" />

        {/* Trash */}
        <button
          type="button"
          onClick={remove}
          aria-label="Delete question"
          className="p-1.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <IconTrash1 />
        </button>
      </div>

      {/* ── Body ── */}
      <div className="px-4 pb-4 space-y-3">
        {/* Question label + variable picker */}
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Question label
          </label>
          <FormValidator error={errors["text"]} errorClassName="absolute">
            <div className="flex items-center gap-2">
              <input
                ref={labelInputRef}
                type="text"
                className="input flex-1 min-w-0"
                required
                placeholder="Enter question text..."
                value={question.text}
                onChange={(e) =>
                  update({ ...question, text: e.currentTarget.value })
                }
              />
              <VariablePicker onInsert={insertVariableIntoLabel} />
            </div>
          </FormValidator>
        </div>

        {/* Variable + Required row */}
        {typeHasInput && (
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Variable name
              </label>
              <input
                disabled={variableFixed}
                type="text"
                className={`input w-full ${variableFixed ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
                placeholder="variable_name"
                value={variableDisplay || question.variable || ""}
                onChange={(e) =>
                  update({ ...question, variable: e.currentTarget.value })
                }
              />
              {(variableDisplay || question.variable) && (
                <div className="mt-1 inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 font-mono text-[11px] px-2 py-0.5 rounded-full">
                  <span className="opacity-60">{"<<"}</span>
                  <span>{variableDisplay || question.variable}</span>
                  <span className="opacity-60">{">>"}</span>
                </div>
              )}
              {variableFixed && (
                <div className="mt-1 text-[11px] text-slate-400">
                  Fixed for this question type
                </div>
              )}
            </div>

            {/* Required toggle */}
            <div className="shrink-0 pt-5">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() =>
                    update({ ...question, required: !question.required })
                  }
                  role="checkbox"
                  aria-checked={question.required}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter")
                      update({ ...question, required: !question.required });
                  }}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                    question.required ? "bg-blue-500" : "bg-slate-200"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      question.required ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </div>
                <span className="text-xs font-medium text-slate-600">Required</span>
              </label>
            </div>

            {/* Satisfaction metric (only for star / NPS) */}
            {canTrackSatisfaction(question.type) && (
              <div className="shrink-0">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Track as metric
                </label>
                <Select
                  options={SATISFACTION_OPTIONS}
                  className="input text-sm py-1"
                  value={question.satisfactionMetricType || ""}
                  onChange={(val) =>
                    update({
                      ...question,
                      satisfactionMetricType: val || undefined
                    })
                  }
                />
              </div>
            )}
          </div>
        )}

        {/* Options section */}
        {typeHasOptions && (
          <div>
            {question.type === "crmField" ? (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  NetSuite field
                </label>
                <Select
                  options={netSuiteOptions}
                  className="input w-full"
                  value={question.variable || ""}
                  onChange={(value) => {
                    const selectedOption = netSuiteOptions.find(
                      (opt) => opt.value === value
                    );
                    if (
                      selectedOption &&
                      "recordType" in selectedOption &&
                      selectedOption.recordType
                    ) {
                      update({
                        ...question,
                        variable: value,
                        crmOptions: {
                          recordType: selectedOption.recordType,
                          netSuiteField: selectedOption.netSuiteField
                        }
                      });
                    } else {
                      update({
                        ...question,
                        variable: value,
                        crmOptions: undefined
                      });
                    }
                  }}
                />
              </div>
            ) : (question.type as string) === "language" ? (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Limit to languages (blank = all)
                </label>
                <ReactSelect
                  isMulti
                  name="languages"
                  options={languageOptionsList.map((lang) => ({
                    value: lang,
                    label: lang
                  }))}
                  value={(question.options || [])
                    .filter((o) => o.trim())
                    .map((lang) => ({ value: lang, label: lang }))}
                  className="basic-multi-select w-full"
                  classNamePrefix="select"
                  placeholder="Select languages (blank = all)..."
                  menuPortalTarget={document.body}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "40px" }),
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      maxHeight: "120px",
                      overflowY: "auto"
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  onChange={(selected) => {
                    const val = selected ? selected.map((item) => item.value) : [];
                    update({ ...question, options: val });
                  }}
                />
                <div className="mt-1 text-xs text-slate-400">
                  {question.options && question.options.length > 0
                    ? `${question.options.filter((o) => o.trim()).length} language(s) selected`
                    : `Blank = all ${languageOptionsList.length} languages`}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Options{" "}
                  <span className="font-normal text-slate-400">(one per line)</span>
                </label>
                <textarea
                  className="input w-full resize-none"
                  rows={4}
                  placeholder={"Option 1\nOption 2\nOption 3"}
                  value={optionsList}
                  onChange={(e) => {
                    const val = e.currentTarget.value.split("\n");
                    update({ ...question, options: val });
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Skip logic ── */}
        <div className="border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={toggleSkipLogic}
            disabled={watchableQuestions.length === 0 && !showSkipLogic}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              watchableQuestions.length === 0 && !showSkipLogic
                ? "text-slate-300 cursor-not-allowed"
                : showSkipLogic
                  ? "text-red-500 hover:text-red-600"
                  : "text-blue-500 hover:text-blue-600"
            }`}
            title={
              watchableQuestions.length === 0 && !showSkipLogic
                ? allQuestions.slice(0, index).length === 0
                  ? "Add at least one question before this one to enable conditional display"
                  : "The questions before this one are label/divider types — add a question with an answer field (text, name, dropdown, etc.) before this one"
                : undefined
            }
          >
            {showSkipLogic ? (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Remove condition
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Show only when...
              </>
            )}
          </button>

          {showSkipLogic && cond && (
            <div className="mt-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-2">
                Conditional display
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 font-medium">Show when</span>

                {/* Which question to watch */}
                <select
                  className="input text-sm py-1 pr-6"
                  value={cond.questionIndex}
                  onChange={(e) =>
                    updateCondition({ questionIndex: Number(e.currentTarget.value) })
                  }
                >
                  {watchableQuestions.map(({ q, qi }) => (
                    <option key={qi} value={qi}>
                      Q{qi + 1}: {q.text ? q.text.slice(0, 40) : `(untitled)`}
                      {q.variable ? ` — <<${q.variable}>>` : ""}
                    </option>
                  ))}
                </select>

                {/* Operator */}
                <select
                  className="input text-sm py-1 pr-6"
                  value={cond.operator}
                  onChange={(e) =>
                    updateCondition({
                      operator: e.currentTarget.value as VisibilityCondition["operator"]
                    })
                  }
                >
                  {OPERATOR_OPTIONS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {/* Value (hidden for is_empty / is_not_empty) */}
                {cond.operator !== "is_empty" &&
                  cond.operator !== "is_not_empty" && (
                    <input
                      type="text"
                      className="input text-sm py-1 w-32"
                      placeholder="value..."
                      value={cond.value}
                      onChange={(e) =>
                        updateCondition({ value: e.currentTarget.value })
                      }
                    />
                  )}
              </div>
              <p className="mt-2 text-[11px] text-blue-500 leading-relaxed">
                This question will only appear when the condition above is true.
                Hidden questions are skipped automatically — their answers won&apos;t be
                submitted.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
